import { $applyNodeReplacement, TextNode } from "lexical";
import { addClassNamesToElement } from "@lexical/utils";

// Derived from packages/lexical-playground/src/nodes/MentionNode.ts in the
// Lexical repository (MIT). Lexical publishes no mention node, and the
// backend reads userId off this node to raise mention notifications, so the
// node is owned here rather than pulled from a third-party package.
export class MentionNode extends TextNode {
  static getType() {
    return "mention";
  }

  static clone(node) {
    return new MentionNode(
      node.__mention,
      node.__userId,
      node.__text,
      node.__key
    );
  }

  static importJSON(serializedNode) {
    return $createMentionNode(
      serializedNode.mentionName,
      serializedNode.userId
    ).updateFromJSON(serializedNode);
  }

  static importDOM() {
    return {
      span: domNode =>
        domNode.hasAttribute("data-lexical-mention")
          ? { conversion: $convertMentionElement, priority: 1 }
          : null,
    };
  }

  constructor(mentionName, userId, text, key) {
    super(text ?? mentionName, key);
    this.__mention = mentionName;
    this.__userId = userId;
  }

  exportJSON() {
    return {
      ...super.exportJSON(),
      mentionName: this.__mention,
      userId: this.__userId,
    };
  }

  createDOM(config) {
    const dom = super.createDOM(config);
    addClassNamesToElement(dom, config.theme.mention);
    this.decorateMentionDOM(dom);
    return dom;
  }

  // The mention email renders rich_text, so the marker attributes have to
  // survive $generateHtmlFromNodes as well as the editor's own DOM.
  exportDOM() {
    const element = document.createElement("span");
    this.decorateMentionDOM(element);
    element.textContent = this.__text;
    return { element };
  }

  decorateMentionDOM(element) {
    element.setAttribute("data-lexical-mention", "true");
    if (this.__userId != null) {
      element.setAttribute("data-mention-id", String(this.__userId));
    }
  }

  isTextEntity() {
    return true;
  }

  canInsertTextBefore() {
    return false;
  }

  canInsertTextAfter() {
    return false;
  }
}

function $convertMentionElement(domNode) {
  const textContent = domNode.textContent;
  if (textContent === null) {
    return null;
  }
  const userId = domNode.getAttribute("data-mention-id");
  return {
    node: $createMentionNode(textContent, userId ? Number(userId) : null),
  };
}

export function $createMentionNode(mentionName, userId) {
  const node = new MentionNode(mentionName, userId);
  node.setMode("segmented").toggleDirectionless();
  return $applyNodeReplacement(node);
}

export function $isMentionNode(node) {
  return node instanceof MentionNode;
}
